// London's trains, apart from the page that shows them: the network from
// TfL's routes, TfL's predictions on the page's lines, and a train's run
// for its card. Used by London's page (app.ts) and the world map
// (apps/world).

import * as Core from '@tol/core/core';
import type { GeometryFile, Network, RouteResponse } from '@tol/core/types';
import type { CardTrain, TripRow } from '@tol/ui/card';
import { NORTHERN_CX, pageLines, splitRoutes, tidyNames } from './lines.ts';
import type { TflPrediction } from '../shared/tfl.ts';

// A TfL arrival prediction, as far as relabeling its line needs (pageLines).
export function isTflPrediction(v: unknown): v is TflPrediction {
  return (
    typeof v === 'object' &&
    v !== null &&
    'lineId' in v &&
    typeof v.lineId === 'string' &&
    (!('towards' in v) || v.towards === undefined || typeof v.towards === 'string') &&
    (!('stationName' in v) || v.stationName === undefined || typeof v.stationName === 'string') &&
    (!('vehicleId' in v) || v.vehicleId === undefined || typeof v.vehicleId === 'string')
  );
}

// An arrivals response with each prediction on the page's line (the Northern
// line's trains via Charing Cross are the Charing Cross line's).
export function withPageLines(list: readonly unknown[]): unknown[] {
  const predictions = list.filter((p) => isTflPrediction(p));
  const lines = pageLines(predictions);
  const lineOf = new Map(predictions.map((p, i) => [p, lines[i]]));
  return list.map((p) => (isTflPrediction(p) ? Object.assign({}, p, { lineId: lineOf.get(p) }) : p));
}

// The network from every route so far and the track shapes (null: straight
// lines). The Charing Cross line runs on the Northern line's track shapes.
export function londonNetwork(responses: readonly RouteResponse[], geometry: GeometryFile | null): Network {
  const shapes = geometry
    ? { ...geometry, segments: { ...geometry.segments, [NORTHERN_CX]: geometry.segments?.['northern'] ?? {} } }
    : null;
  return Core.attachGeometry(tidyNames(Core.buildNetwork(splitRoutes(responses))), shapes);
}

// A train's run: the stations it has passed (from the line, no times), TfL's
// predictions (about a quarter of an hour ahead), then the rest of the run
// to the destination with times estimated from the one before (segTime:
// its run and stop).
export function londonTripRows(t: CardTrain, net: Network, now: number): readonly TripRow[] {
  const p = t.pred;
  const upcoming = p.stops.filter((s) => s.eta > now - 30000);
  const line = net.lines[p.lineId];
  const ahead = upcoming.map((s) => s.sid);
  const destId =
    [...(line?.ids ?? [])].find((id) => Core.normName(net.stations.get(id)?.name) === Core.normName(p.dest)) ?? null;
  const rows: TripRow[] = Core.stationsBehind(line, ahead).map((sid) => ({
    sid,
    kind: 'past',
    time: 0,
    source: 'none',
  }));
  for (const [i, s] of upcoming.entries())
    rows.push({ sid: s.sid, kind: i === 0 ? 'next' : 'ahead', time: s.eta, source: 'predicted' });
  const last = upcoming.at(-1);
  let at = last?.sid;
  let eta = last?.eta ?? 0;
  for (const sid of Core.stationsAfter(line, ahead, destId)) {
    if (at !== undefined) eta += Core.segTime(net, at, sid, p.lineId) * 1000;
    at = sid;
    rows.push({ sid, kind: 'ahead', time: last ? eta : 0, source: last ? 'estimated' : 'none' });
  }
  return rows;
}
